import React, { useState, useRef, ChangeEvent, DragEvent } from 'react';
import { Container, Typography, Button, CircularProgress, Alert, Card, Stack, AspectRatio, CardContent, Grid, Box } from '@mui/joy';
import Dashboard from '../dashboard/Dashboard.tsx';
import { Helmet } from 'react-helmet-async';
import { useImageGen } from './useImageGen.ts';

// Reusable styled components
const StepContainer = ({ children, maxWidth = '700px' }: { children: React.ReactNode; maxWidth?: string }) => (
  <Stack spacing={2.5} sx={{ width: '100%', maxWidth, alignItems: 'center', p: 2, borderRadius: 'md', border: '1px solid', borderColor: 'divider' }}>
    {children}
  </Stack>
);

const PreviewImage = ({ src, size = 180 }: { src: string; size?: number }) => (
  <Box sx={{ mb: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <Typography level="body-sm" textAlign="center" sx={{ mb: 0.5, mt: 0.5 }}>Your Image:</Typography>
    <Card variant="outlined" sx={{ maxWidth: size, p: 1 }}>
      <AspectRatio ratio="0.666" sx={{ width: size - 30, margin: 'auto', borderRadius: 'sm' }}>
        <img src={src} alt="Current selection" style={{ objectFit: 'cover' }} />
      </AspectRatio>
    </Card>
  </Box>
);

const NavigationButtons = ({ 
  onBack, 
  onNext, 
  backText, 
  nextText, 
  nextDisabled = false 
}: { 
  onBack: () => void; 
  onNext: () => void; 
  backText: string; 
  nextText: string; 
  nextDisabled?: boolean;
}) => (
  <Stack direction="row" spacing={2} sx={{ mt: 2.5, width: '100%', justifyContent: 'space-between' }}>
    <Button onClick={onBack} variant="outlined" sx={{ flexGrow: 1, maxWidth: 'calc(50% - 8px)' }}>
      {backText}
    </Button>
    <Button
      onClick={onNext}
      disabled={nextDisabled}
      fullWidth
      color="primary"
      sx={{ flexGrow: 1, maxWidth: 'calc(50% - 8px)' }}
    >
      {nextText}
    </Button>
  </Stack>
);

const LoadingState = ({ message = "Loading..." }: { message?: string }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, my: 2 }}>
    <CircularProgress size="md" />
    <Typography level="body-sm">{message}</Typography>
  </Box>
);

export const ImageGen = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const {
    isLoading,
    error,
    generatedImageUrl,
    generateImage,
    setError,
    setGeneratedImageUrl,
    availableModels,
    selectedCategory,
    setSelectedCategory,
    selectedStyle,
    setSelectedStyle,
  } = useImageGen();

  const goToNextStep = () => setCurrentStep(prev => prev + 1);
  const goToPreviousStep = () => setCurrentStep(prev => prev - 1);

  const handleStartOver = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setGeneratedImageUrl(null);
    setError(null);
    setSelectedModelId(null);
    setCurrentStep(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFile(file);
      setGeneratedImageUrl(null);
      setError(null);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null);
      setPreviewUrl(null);
    }
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    const items = e.dataTransfer.items;
    if (items && items[0] && items[0].type.startsWith('image/')) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const items = e.dataTransfer.items;
    e.dataTransfer.dropEffect = items && items[0] && items[0].type.startsWith('image/') ? 'copy' : 'none';
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
        setGeneratedImageUrl(null);
        setError(null);

        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const triggerImageGenerationAndProceed = async () => {
    if (previewUrl && selectedFile && selectedModelId) {
      setCurrentStep(4);
      try {
        await generateImage({ 
          base64Image: previewUrl, 
          modelId: selectedModelId
        });
      } catch (error) {
        console.error("Error during image generation:", error);
      }
    }
  };

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadClick = async () => {
    if (!generatedImageUrl) return;

    try {
      const response = await fetch(generatedImageUrl);
      if (!response.ok) {
        let errorBody = '';
        try {
          errorBody = await response.text();
        } catch (e) {
          // Ignore if can't read body
        }
        throw new Error(`HTTP error! status: ${response.status}. ${errorBody}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `generated_photo_${Date.now()}.png`);
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
      }, 100);

    } catch (downloadError) {
      console.error("Download failed:", downloadError);
      setError(downloadError instanceof Error ? `Download failed: ${downloadError.message}` : "Failed to download image. Please try again or save the image from the preview.");
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <StepContainer maxWidth="500px">
            <input
              type="file"
              accept="image/png, image/jpeg, image/webp"
              onChange={handleFileChange}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            {!selectedFile ? (
              <Box
                onClick={handleSelectFileClick}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                sx={(theme) => ({
                  width: '100%',
                  height: '200px',
                  border: '2px dashed',
                  borderColor: isDragging ? theme.palette.primary[500] : theme.palette.neutral[300],
                  borderRadius: 'md',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  bgcolor: isDragging ? theme.palette.primary[50] : theme.palette.background.surface,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary[500],
                    bgcolor: theme.palette.primary[50],
                  }
                })}
              >
                <Typography level="body-lg" textAlign="center">
                  {isDragging ? 'Drop your image here' : 'Drag and drop an image here'}
                </Typography>
                <Typography level="body-sm" textColor="neutral.500">
                  or click to select a file
                </Typography>
              </Box>
            ) : (
              <Card variant="outlined" sx={{ width: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography level="title-sm">Your Selected Image:</Typography>
                    <Button
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={() => {
                        setSelectedFile(null);
                        setPreviewUrl(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                  <AspectRatio ratio="0.666" sx={{ minWidth: 200, borderRadius: 'md' }}>
                    <img src={previewUrl as string} alt="Selected preview" style={{ objectFit: 'cover' }} />
                  </AspectRatio>
                </CardContent>
              </Card>
            )}

            {selectedFile && previewUrl && (
              <Button onClick={goToNextStep} fullWidth color="primary" sx={{ mt: 2 }}>
                Next: Select Category
              </Button>
            )}
          </StepContainer>
        );

      case 1:
        return (
          <StepContainer>
            {previewUrl && <PreviewImage src={previewUrl} />}

            {isLoading && availableModels && Object.keys(availableModels).length === 0 && !error && (
              <LoadingState message="Loading available categories..." />
            )}

            {error && availableModels && Object.keys(availableModels).length === 0 && (
              <Alert color="danger" variant="soft" sx={{ width: '100%' }}>
                <Typography fontWeight="md">Error loading categories:</Typography>
                {error}
              </Alert>
            )}

            {availableModels && Object.keys(availableModels).length > 0 && (
              <Box sx={{ width: '100%' }}>
                <Typography level="title-md" textAlign="center" sx={{ mb: 1.5 }}>Select a Category:</Typography>
                <Grid container spacing={1.5} sx={{ mb: 3 }}>
                  {Object.keys(availableModels).map((category) => (
                    <Grid key={category} xs={6} sm={4} md={3}>
                      <Button
                        variant={selectedCategory === category ? "solid" : "outlined"}
                        onClick={() => {
                          setSelectedCategory(category);
                          const styles = Object.keys(availableModels[category]);
                          if (styles.length > 0) {
                            setSelectedStyle(styles[0]);
                          }
                        }}
                        sx={(theme) => ({
                          width: '100%',
                          height: '100%',
                          minHeight: '120px',
                          p: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          transition: 'all 0.2s ease',
                          color: selectedCategory === category ? theme.palette.primary.solidColor : 'inherit',
                          '& .MuiTypography-root': {
                            color: selectedCategory === category ? theme.palette.primary.solidColor : 'inherit'
                          }
                        })}
                      >
                        <AspectRatio ratio="0.666" sx={{ width: '100%', borderRadius: 'sm', overflow: 'hidden' }}>
                          <img
                            src={availableModels[category].cover}
                            alt={category}
                            style={{ objectFit: 'cover' }}
                          />
                        </AspectRatio>
                        <Typography level="body-sm" textAlign="center">
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </Typography>
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            <NavigationButtons
              onBack={goToPreviousStep}
              onNext={goToNextStep}
              backText="Back: Change Photo"
              nextText="Next: Select Style"
              nextDisabled={!selectedCategory || !availableModels || Object.keys(availableModels).length === 0}
            />
          </StepContainer>
        );

      case 2:
        return (
          <StepContainer maxWidth="900px">
            {previewUrl && <PreviewImage src={previewUrl} />}

            {selectedCategory && availableModels?.[selectedCategory] && (
              <Box sx={{ width: '100%' }}>
                <Typography level="title-md" textAlign="center" sx={{ mb: 1.5 }}>Select a Style:</Typography>
                <Grid container spacing={1.5} sx={{ mb: 3 }}>
                  {Object.keys(availableModels[selectedCategory])
                    .filter(style => style !== 'cover')
                    .map((style) => (
                    <Grid key={style} xs={6} sm={4} md={3}>
                      <Button
                        variant={selectedStyle === style ? "solid" : "outlined"}
                        onClick={() => setSelectedStyle(style)}
                        sx={(theme) => ({
                          width: '100%',
                          height: '100%',
                          minHeight: '120px',
                          p: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          transition: 'all 0.2s ease',
                          color: selectedStyle === style ? theme.palette.primary.solidColor : 'inherit',
                          '& .MuiTypography-root': {
                            color: selectedStyle === style ? theme.palette.primary.solidColor : 'inherit'
                          }
                        })}
                      >
                        <AspectRatio ratio="0.666" sx={{ width: '100%', borderRadius: 'sm', overflow: 'hidden' }}>
                          <img
                            src={availableModels[selectedCategory][style]['front']?.url || 
                                 availableModels[selectedCategory][style][Object.keys(availableModels[selectedCategory][style])[0]]?.url}
                            alt={style}
                            style={{ objectFit: 'cover' }}
                          />
                        </AspectRatio>
                        <Typography level="body-sm" textAlign="center">
                          {style.charAt(0).toUpperCase() + style.slice(1)}
                        </Typography>
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            <NavigationButtons
              onBack={goToPreviousStep}
              onNext={goToNextStep}
              backText="Back: Change Category"
              nextText="Next: Select Camera Angle"
              nextDisabled={!selectedStyle}
            />
          </StepContainer>
        );

      case 3:
        return (
          <StepContainer maxWidth="900px">
            {previewUrl && <PreviewImage src={previewUrl} />}

            {selectedCategory && selectedStyle && availableModels?.[selectedCategory]?.[selectedStyle] && (
              <Box sx={{ width: '100%' }}>
                <Typography level="title-md" textAlign="center" sx={{ mb: 1.5 }}>Select the camera angle:</Typography>
                <Grid container spacing={1.5}>
                  {Object.entries(availableModels[selectedCategory][selectedStyle]).map(([camera, model]) => {
                    const photoModel = model as { url: string; name: string; model: string };
                    return (
                      <Grid key={camera} xs={4} sm={3} md={3}>
                        <Button
                          variant={selectedModelId === `${selectedCategory}_${selectedStyle}_${camera}` ? "solid" : "outlined"}
                          onClick={() => setSelectedModelId(`${selectedCategory}_${selectedStyle}_${camera}`)}
                          sx={(theme) => ({
                            width: '100%',
                            height: '100%',
                            minHeight: '120px',
                            p: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            transition: 'all 0.2s ease',
                            color: selectedModelId === `${selectedCategory}_${selectedStyle}_${camera}` ? theme.palette.primary.solidColor : 'inherit',
                            '& .MuiTypography-root': {
                              color: selectedModelId === `${selectedCategory}_${selectedStyle}_${camera}` ? theme.palette.primary.solidColor : 'inherit'
                            }
                          })}
                        >
                          <AspectRatio ratio="0.666" sx={{ width: '100%', borderRadius: 'sm', overflow: 'hidden' }}>
                            <img
                              src={photoModel.url}
                              alt={`Camera angle: ${photoModel.name}`}
                              style={{ objectFit: 'cover' }}
                            />
                          </AspectRatio>
                          <Typography level="body-sm" textAlign="center">
                            {photoModel.name}
                          </Typography>
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            <NavigationButtons
              onBack={goToPreviousStep}
              onNext={triggerImageGenerationAndProceed}
              backText="Back: Change Style"
              nextText="Generate Photo"
              nextDisabled={!selectedModelId}
            />
          </StepContainer>
        );

      case 4:
        return (
          <StepContainer>
            {isLoading && !generatedImageUrl && !error && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, my: 3, p: 3, borderRadius: 'md', width: '100%' }}>
                <CircularProgress size="lg" />
                <Typography level="title-md">Generating your photo...</Typography>
                <Typography level="body-sm">This may take a moment.</Typography>
              </Box>
            )}

            {error && (
              <Alert color="danger" variant="soft" sx={{ width: '100%' }}>
                <Typography fontWeight="bold">Generation Failed</Typography>
                {error}
              </Alert>
            )}

            {generatedImageUrl && !isLoading && !error && (
              <Card variant="outlined" sx={{ width: '100%' }}>
                <CardContent>
                  <Typography level="h4" component="h2" sx={{ mb: 1 }}>Generated Photo:</Typography>
                  <AspectRatio ratio="0.666" sx={{ minWidth: 300, borderRadius: 'md' }}>
                    <img src={generatedImageUrl} alt="Generated product" style={{ objectFit: 'cover' }} />
                  </AspectRatio>
                  <Button
                    variant="solid"
                    color="primary"
                    onClick={handleDownloadClick}
                    sx={{ mt: 2, width: '100%' }}
                  >
                    Download Image
                  </Button>
                </CardContent>
              </Card>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 2, width: '100%', justifyContent: 'space-between' }}>
              <Button onClick={() => setCurrentStep(3)} variant="outlined" sx={{ flexGrow: 1, maxWidth: 'calc(50% - 8px)' }} disabled={isLoading}>
                Change Camera Angle
              </Button>
              <Button onClick={handleStartOver} sx={{ flexGrow: 1, maxWidth: 'calc(50% - 8px)' }} disabled={isLoading} color="neutral" variant="soft">
                Start Over
              </Button>
            </Stack>
          </StepContainer>
        );

      default:
        return null;
    }
  };

  return (
    <Dashboard>
      <Helmet>
        <title>Generate Product Photo</title>
        <meta name="description" content="Generate a new product photo using AI by providing an image and selecting a model outfit." />
        <meta property="og:title" content="Poshwatch AI Image Generator" />
        <meta property="og:description" content="Upload an image and choose a model to generate a new product photo." />
        <meta property="og:url" content="https://poshwatch.io/image-gen" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: { xs: 2, sm: 4, md: 8 },
          gap: 3,
          maxWidth: 'lg'
        }}
      >
        <Typography level="h2" component="h1" textAlign="center">
          AI Product Photo Generator
        </Typography>
        <Typography level="body-lg" textAlign="center" sx={{ mb: 1 }}>
          {currentStep === 0 && "Step 1: Select an image."}
          {currentStep === 1 && "Step 2: Select a category."}
          {currentStep === 2 && "Step 3: Select a style."}
          {currentStep === 3 && "Step 4: Select a camera angle."}
          {currentStep === 4 && (isLoading && !generatedImageUrl && !error ? "Generating your photo..." : (generatedImageUrl ? "Step 5: Your photo is ready!" : (error ? "Step 5: Generation Failed" : "Step 5: Review your result.")))}
        </Typography>

        {renderStepContent()}
      </Container>
    </Dashboard>
  );
};
